import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/hooks/useAuth';
import { authApi } from '@/lib/api/auth';

export const GoogleCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const refreshToken = searchParams.get('refreshToken');
    const oauthError = searchParams.get('error');

    if (oauthError) {
      setError(
        oauthError === 'google_cancelled'
          ? 'Google sign-in was cancelled.'
          : 'Google sign-in failed. Please try again.'
      );
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    if (!token || !refreshToken) {
      setError('Invalid callback — missing tokens.');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    // Tokens in hand — fetch full user and set session
    (async () => {
      try {
        // Temporarily store tokens so authApi.getCurrentUser can read them
        localStorage.setItem('accessToken', token);
        localStorage.setItem('refreshToken', refreshToken);

        const user = await authApi.getCurrentUser();
        setSession(token, refreshToken, user);
        navigate('/dashboard', { replace: true });
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setError('Failed to load your account. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <span className="material-symbols-outlined text-red-500 text-5xl mb-4 block">error</span>
            <p className="text-gray-700 font-medium">{error}</p>
            <p className="text-gray-500 text-sm mt-2">Redirecting to login…</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 font-medium">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
};
